<template>
  <div class="ma-4">
    <v-snackbar
      v-model="msg"
      top
      :color="snackbarColor"
      :timeout="3000"
      elevation="4"
      class="rounded-lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{
          snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
        }}</v-icon>
        {{ text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="msg = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3">
          Unsigned ADL Report
        </span>

        <v-spacer></v-spacer>

        <report-list-actions
          :reportOptions="reportOptions"
          :downloadFilename="reportListFilename"
          class="mr-2"
        />
      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense>
          <v-col cols="12" md="2">
            <v-menu
              v-model="calendarMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.startDate"
                  label="Start Date"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                  :rules="requiredField"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.startDate"
                @input="calendarMenu = false"
                color="primary"
                class="rounded-lg"
                :rules="requiredField"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              v-model="calendar2Menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.endDate"
                  label="End Date"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.endDate"
                @input="calendar2Menu = false"
                color="primary"
                class="rounded-lg"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Filters -->
          <v-col cols="12" md="2">
            <v-select
              v-model="reportFilters.clientStatus"
              :items="clientStatuses"
              label="Client Status"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
              @change="fetchADLReport"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="reportFilters.program"
              :items="programs"
              item-text="Name"
              item-value="Name"
              label="Select Program"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
              @change="fetchADLReport"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="reportFilters.ClientID"
              :items="filteredClients"
              item-text="Name"
              item-value="_id"
              label="Select a Client"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              block
              height="40"
              class="rounded-lg"
              :loading="isLoading"
              elevation="1"
              @click="fetchADLReport"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col cols="12" md="3">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              item-text="text"
              item-value="value"
              label="Sort by"
              dense
              outlined
              hide-details
              class="rounded-lg"
              background-color="white"
              @change="onSortChange"
            ></v-select>
          </v-col>
          
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              outlined
              :color="sortDesc ? 'primary' : 'grey'"
              height="40"
              class="rounded-lg"
              @click="toggleSortDirection"
            >
              <v-icon left>{{ sortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
              {{ sortDesc ? 'Descending' : 'Ascending' }}
            </v-btn>
          </v-col>

          <v-col cols="12" md="7">
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text class="pa-6">
        <v-data-table
          :headers="headers"
          :items="adls"
          :loading="isLoading"
          loading-text="Loading ADLs..."
          :page="page"
          :pageCount="pageCount"
          :server-items-length="itemCount"
          :options.sync="options"
          group-by="client.name"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
          class="elevation-1 rounded-lg"
        >
          <template v-slot:item.status="{ item }">
            <v-chip
              :color="item.status === 'Unsigned' ? 'warning' : 'success'"
              small
              class="px-2"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.select="{ item }">
            <v-btn x-small color="primary" @click="viewADL(item)" class="px-3">
              <v-icon left x-small>mdi-eye</v-icon>
              View
            </v-btn>
          </template>
          <template v-slot:group.header="{ group, headers, toggle, isOpen }">
            <td :colspan="headers.length" class="blue-grey lighten-5 pa-3">
              <v-btn @click="toggle" x-small icon :color="isOpen ? 'primary' : 'grey'">
                <v-icon>{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </v-btn>
              <strong class="ml-2 text-h6">{{ group }}</strong>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- View/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="75%" class="rounded-lg">
      <v-card>
        <v-card-title class="primary white--text">
          <span class="text-h6">{{ titleForm }}</span>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newService"
                  label="Service"
                  outlined
                  dense
                  disabled
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newTime"
                  label="ADL Time"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="newAssistance"
                  label="Level of Assistance"
                  :items="assistance"
                  :rules="requiredRules"
                  outlined
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="newException"
                  label="Exception"
                  :items="exception"
                  :rules="requiredRules"
                  outlined
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-textarea
                  v-model="newNotes"
                  label="Notes"
                  outlined
                  auto-grow
                  rows="4"
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newSignature"
                  label="Signature"
                  :append-icon="canUpdate ? 'mdi-pencil' : undefined"
                  @click:append="onUpdateSignature"
                  outlined
                  dense
                  :readonly="!canUpdate"
                  :disabled="!canUpdate"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :loading="isUpdating"
            @click="onUpdateItem"
            v-if="editADL"
          >
            Update
          </v-btn>
          <v-btn text @click="close"> Cancel </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import axios from "axios";
import moment from "moment";
import { setUserTz, tz } from "../../../utils/timezone";
import { STATUS_ARRAY } from "../../enums/UserStatusEnum";
import DateTimeUtil from "../../../utils/date-time.util";
import ReportHeader from "@/components/Report/ReportHeader.vue";
import ReportListActions from "@/components/Report/ReportListActions.vue";
import ReportFilters from "@/mixins/reportFilters.js";

const url = require("../../client_config/config").url;

export default {
  mixins: [ReportFilters],
  props: {
    itemData: {
      type: Object,
    },
    updatedSignature: {
      type: String,
    },
  },
  components: {
    ReportHeader,
    ReportListActions,
  },
  data: () => ({
    headers: [
      {
        text: "Date",
        align: "start",
        sortable: true,
        value: "date.formatted",
      },
      { text: "Client", value: "client.name" },
      { text: "Program", value: "client.program" },
      { text: "Service", value: "service.item" },
      { text: "Scheduled Time", value: "time" },
      // { text: "Notes", value: "service.notes" },
      // { text: "Signature", value: "staff.signature" },
      // { text: "Actions", value: "select", sortable: false },
    ],
    // Server-side sort options
    sortBy: 'Date',
    sortDesc: true,
    sortOptions: [
      { text: 'Date', value: 'Date' },
      { text: 'Client Name', value: 'client.name' },
      { text: 'Program', value: 'client.program' },
      { text: 'Service', value: 'service.item' },
      { text: 'Scheduled Time', value: 'time' },
    ],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    clients: [],
    adls: [],
    programs: [],
    temp: [],
    status: ["Signed", "Unsigned"],
    selectedStatus: "",
    calendarMenu: false,
    calendar2Menu: false,
    report: "",
    clientName: "",
    isLoading: false,
    userState: localStorage.getItem("userState"),
    states: ["Utah", "Nevada"],
    isUtahIncidentReports: true,
    requiredField: [(v) => !!v || "This is required"],
    model: "tab-2",
    text: "",
    dialog: false,
    assistance: [
      "Independent",
      "Minimal Assistance",
      "Moderate Assistance",
      "Total Assistance",
    ],
    exception: ["Refused", "ADL on Hold", "Absent", "Other"],
    titleForm: "Viewing Unsigned ADL",
    newService: "",
    newTime: "",
    newAssistance: "",
    newException: "",
    newNotes: "",
    editADL: false,
    newSignature: "",
    adlID: "",
    msg: "",
    requiredRules: [(v) => !!v || "This is required"],
    page: 1,
    pageCount: 1,
    itemCount: 0,
    options: {},
    clientStatus: "",
    clientStatuses: STATUS_ARRAY,
    filteredClients: [],
    StaffID: localStorage.getItem("staffId"),
    reportFilters: {
      startDate: "",
      endDate: "",
      ClientID: "",
      clientStatus: "",
      program: "",
    },
    reportFilterName: "ReportFilters_UnsignedADL",
    snackbarColor: "success",
  }),

  computed: {
    ...mapGetters("timezones", ["getUserTimezone", "getDefaultTz"]),

    userTimezone() {
      const data = {
        userTimezone: this.getUserTimezone,
        defaultTz: this.getDefaultTz,
      };
      return setUserTz(data);
    },

    staffName() {
      return localStorage.getItem("Name");
    },

    queryParams() {
      let { page, itemsPerPage } = this.options;

      page = page || 1;
      itemsPerPage = itemsPerPage || 10;

      let sortDirection;
      if (this.sortBy === 'client.name') {
        sortDirection = this.sortDesc ? 'desc' : 'asc';
      } else {
        sortDirection = this.sortDesc ? 'desc' : 'asc';
      }

      return {
        program: this.reportFilters.program || null,
        startDate: this.reportFilters.startDate || null,
        endDate: this.reportFilters.endDate || null,
        clientId: this.reportFilters.ClientID || null,
        state: this.userState || null,
        clientStatus: this.reportFilters.clientStatus,
        page: page,
        limit: itemsPerPage,
        sortField: this.sortBy,
        sortDirection: sortDirection,
        tz: this.getDefaultTz,
        signature: "",
        unsigned: true,
        generateFile: true,
      };
    },

    reportOptions() {
      let query = {
        name: this.staffName,
      };

      Object.assign(query, this.queryParams);

      return {
        query: query,
        endpoints: `reports/create/adls-report`,
        dynamic: true,
      };
    },
    
    reportListFilename() {
      return `${this.staffName} - Unsigned ADL Report`;
    },
  },

  watch: {
    clientStatus: function (val) {
      if (val == "" || val == null) this.filteredClients = this.clients;
      else
        this.filteredClients = this.clients.filter(
          (client) => client.Status == val
        );
    },
    options: {
      handler() {
        this.fetchADLReport();
      },
      deep: true,
    },
    reportFilters: {
      handler(newVal) {
        this.setReportFilters(this.reportFilterName, newVal);
      },
      deep: true,
    },
  },

  created() {
    this.getClients();
    this.getPrograms();

    this.reportFilters =
      this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
  },

  methods: {
    onSortChange() {
      if (this.sortBy === 'client.name') {
        this.sortDesc = false;
      }
      this.fetchADLReport();
    },

    toggleSortDirection() {
      this.sortDesc = !this.sortDesc;
      this.fetchADLReport();
    },

    initialize() {
      this.clients = [];
    },

    canUpdate() {
      if (
        localStorage.getItem("userRole") == "Administration" ||
        localStorage.getItem("userRole") == "Program Coordinator" ||
        localStorage.getItem("userRole") == "Client Services Manager" ||
        localStorage.getItem("userRole") == "Medical Manager" ||
        localStorage.getItem("userRole") == "Compliance Manager" ||
        localStorage.getItem("userRole") == "Human Resources"
      ) {
        return true;
      }
      return false;
    },

    async getClients() {
      let params = {};
      let endpoint = `${url}/clients`;

      const companyIds = localStorage.getItem("userCompanies");

      params["Company:in"] = JSON.parse(companyIds);
      params["State"] = this.userState;

      await axios.get(endpoint, { params }).then((res) => {
        this.clients = res.data.clients;
        this.filteredClients = this.clients;
      });
    },

    async getPrograms() {
      await axios.get(`${url}/units/state/${this.userState}`).then((res) => {
        this.programs = res.data.units;
      });
    },

    close() {
      this.dialog = false;
    },

    async fetchADLReport() {
      try {
        this.adls = [];
        this.isLoading = true;

        const response = await axios.get(`${url}/adl/unsigned/allADLReport`, {
          params: this.queryParams,
        });

        const { results, pagination } = response.data;

        this.pageCount = pagination.limit;
        this.itemCount = pagination.total;
        this.adls = results;
      } catch (error) {
        console.error("Failed to fetch ADL report:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    onUpdateItem() {
      var data = {
        Date: this.newDate,
        Time: this.newTime,
        Assistance: this.newAssistance,
        Exception: this.newException,
        Notes: this.newNotes,
        ClientID: this.clientID,
        Signature: this.newSignature,
        UrlNotification: this.$router.history.current.path,
      };
      axios.put(`${url}/adl`, data).then((res) => {});
    },

    viewADL(item) {
      console.log(item + " item");
      this.dialog = true;
      this.newService = item.services.ServiceItem;
      this.newTime = item.services.ScheduledTime;
      this.newAssistance = item.services.Assistance;
      this.newException = item.services.Exception;
      this.newNotes = item.services.Notes;
      this.newSignature = item.services.Signature;
      this.adlID = item._id;
    },

    onUpdateSignature() {
      this.$emit("updateSignature");
      this.$nextTick(() => {
        this.newSignature = this.updatedSignature;
      });
    },
  },
};
</script>

<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>